import {
  SkyBadge,
  SkyButton,
  SkyCard,
  SkyInput,
  SkyLoader,
  SkyMessageBar,
  SkySwitch,
  SkyText,
} from "@skypointcloud/skypoint-ui";
import { useEffect, useMemo, useState } from "react";
import { useLocation } from "wouter";
import { CONNECTORS_LIMITED_NOTE } from "@shared/types/connectorAccess";
import type { ConnectorSummary } from "@shared/types/connectors";
import { ConnectorLogo } from "@/components/connections/ConnectorLogo";
import { useConnectors } from "@/hooks/use-connectors";
import { failureMessage, type ApiClientError } from "@/lib/api";
import { RetryIcon, SearchIcon } from "@/lib/icons";
import { NEW_CONNECTION_PATH, absolutePath } from "@/lib/routes";

/**
 * Choosing which connector a new connection uses, before the form exists
 * (UX-3, FR-30).
 *
 * The gallery is the whole catalog, and the catalog is meant to grow past what
 * fits on one screen (NFR-5 sizes it at fifty), so it carries a search box. The
 * search matches what is ON the cards — display name, description, category —
 * plus the connector's slug, for whoever has a seed file or an API response in
 * their head rather than the marketing name.
 *
 * T-51 (round 8) and round 10 (C1): connectors offered for a new connection (`offer`: available
 * on the platform and enabled by the tenant in Settings > Connectors) come first, alphabetical.
 * Under "Not enabled for your organisation" follow the Disabled ones, greyed and not selectable,
 * each with the reason the server composed (`offerReason`). A Hidden connector never reaches the
 * list. Search filters both groups; "Enabled only" (off by default, not remembered) drops the
 * second.
 */

const NONE_ENABLED =
  "No connectors are enabled for new connections in your organisation. An admin can enable them in Settings.";

/** How long the typist gets between keystrokes before the gallery is re-filtered. */
const SEARCH_DEBOUNCE_MS = 200;

/**
 * The typed value, settled.
 *
 * The input itself paints every keystroke immediately; only the FILTER runs on
 * the settled value. The filter is cheap at catalog scale — what the debounce
 * buys is the live region below the box, which would otherwise read a
 * screen-reader user "8 of 50", "3 of 50", "1 of 50" while they are still
 * typing a word.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [settled, setSettled] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(value), delayMs);
    return () => clearTimeout(timer);
  }, [value, delayMs]);
  return settled;
}

/**
 * Everything a search term may match on, folded once per connector.
 *
 * Joined with newlines rather than spaces so that a term cannot match across
 * the seam between two fields ("server database" must not match a SQL Server
 * card whose first category is Database).
 */
function haystackFor(connector: ConnectorSummary): string {
  return [
    connector.displayName,
    connector.name,
    connector.description ?? "",
    ...connector.categories,
  ]
    .join("\n")
    .toLowerCase();
}

/** A card's logo, name and description, with the reason line under a Disabled one. */
function CardBody({ connector, reason }: { connector: ConnectorSummary; reason?: string | null }) {
  return (
    /* Session 11 (P1): the connector's logo at 28 px beside its name. */
    <div className="flex items-start gap-3">
      <ConnectorLogo iconUrl={connector.iconUrl} name={connector.displayName} />
      <div className="min-w-0">
        <SkyText weight="semibold" block>
          {connector.displayName}
        </SkyText>
        <SkyText size={200} color="muted" block lineClamp={2}>
          {connector.description ?? connector.categories.join(", ")}
        </SkyText>
        {reason ? (
          <SkyText
            size={200}
            block
            testId={`connector-reason-${connector.name}`}
            className="mt-1 font-medium"
          >
            {reason}
          </SkyText>
        ) : null}
      </div>
    </div>
  );
}

/** What the gallery says when nothing is shown: the search term, the tenant's choice, or none. */
function noMatchText(searchTerm: string, narrowed: boolean): string {
  if (searchTerm) return `No connector matches ‘${searchTerm}’.`;
  return narrowed ? NONE_ENABLED : "No connectors are available on this deployment.";
}

/** The enabled cards, each a button that opens a new connection of that connector. */
function EnabledConnectorList({
  connectors,
  navigate,
}: {
  connectors: ConnectorSummary[];
  navigate: (to: string) => void;
}) {
  return (
    <ul className="grid gap-2 md:grid-cols-2" data-testid="connector-picker-enabled">
      {connectors.map((connector) => (
        <li key={connector.name}>
          {/* `SkyCard` with `onClick` renders `role="button"` with
              `tabIndex=0` and activates on Enter and Space, so each card is
              reachable by keyboard without any wrapper. */}
          <SkyCard
            appearance="outline"
            className="cursor-pointer p-4"
            aria-label={`New ${connector.displayName} connection`}
            testId={`connector-option-${connector.name}`}
            onClick={() =>
              navigate(absolutePath(`${NEW_CONNECTION_PATH}?connector=${connector.name}`))
            }
          >
            <CardBody connector={connector} />
          </SkyCard>
        </li>
      ))}
    </ul>
  );
}

/** The connectors not enabled for the organisation, under their own heading, greyed and inert. */
function DisabledConnectorList({ connectors }: { connectors: ConnectorSummary[] }) {
  return (
    <section aria-labelledby="connector-picker-disabled-heading" className="flex flex-col gap-2">
      <SkyText
        as="h3"
        size={300}
        weight="semibold"
        block
        id="connector-picker-disabled-heading"
        testId="connector-picker-disabled-heading"
      >
        Not enabled for your organisation
      </SkyText>
      <ul className="grid gap-2 md:grid-cols-2" data-testid="connector-picker-disabled">
        {connectors.map((connector) => (
          <li key={connector.name}>
            {/* Greyed and inert: no `onClick`, so it is not a button and takes no
                focus; `aria-disabled` says why it does nothing. */}
            <div
              role="group"
              aria-disabled="true"
              aria-label={`${connector.displayName}, not enabled`}
              data-testid={`connector-option-${connector.name}`}
              className="rounded-md border border-border p-4 opacity-60"
            >
              <CardBody connector={connector} reason={connector.offerReason ?? null} />
            </div>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** The gallery under the search box: the empty sentence, or the enabled then the disabled cards. */
function ConnectorResults({
  enabled,
  disabled,
  searchTerm,
  narrowed,
  navigate,
}: {
  enabled: ConnectorSummary[];
  disabled: ConnectorSummary[];
  searchTerm: string;
  narrowed: boolean;
  navigate: (to: string) => void;
}) {
  if (enabled.length + disabled.length === 0) {
    // Naming the term is what tells the user it was the search that emptied
    // the gallery — and echoing it AS TYPED, not folded, so a user who typed
    // a capital sees their own word quoted back.
    return (
      <SkyText as="p" size={300} color="muted" block testId="connector-no-match">
        {noMatchText(searchTerm, narrowed)}
      </SkyText>
    );
  }
  return (
    <>
      {enabled.length === 0 && !searchTerm ? (
        <SkyText as="p" size={300} color="muted" block testId="connector-none-enabled">
          {NONE_ENABLED}
        </SkyText>
      ) : null}
      {enabled.length > 0 ? (
        <EnabledConnectorList connectors={enabled} navigate={navigate} />
      ) : null}
      {disabled.length > 0 ? <DisabledConnectorList connectors={disabled} /> : null}
    </>
  );
}

export function ConnectorPicker() {
  const [, navigate] = useLocation();
  const connectors = useConnectors();
  const [search, setSearch] = useState("");
  const [enabledOnly, setEnabledOnly] = useState(false);

  /** The settled term as the user typed it, for echoing back; and folded, for matching. */
  const searchTerm = useDebouncedValue(search, SEARCH_DEBOUNCE_MS).trim();
  const needle = searchTerm.toLowerCase();

  /** Each connector folded once per catalog, so a keystroke never re-folds fifty cards. */
  const indexed = useMemo(
    () =>
      (connectors.data ?? [])
        .filter((connector) => connector.offer !== "hidden")
        .map((connector) => ({
          connector,
          enabled: (connector.offer ?? "offered") === "offered",
          hay: haystackFor(connector),
        }))
        .sort((a, b) => a.connector.displayName.localeCompare(b.connector.displayName)),
    [connectors.data],
  );
  /** The tenant's choice left an available connector out: the gallery says who can change it. */
  const narrowed = (connectors.data ?? []).some((connector) => connector.offer === "not_selected");
  const matching = useMemo(
    () => (needle ? indexed.filter(({ hay }) => hay.includes(needle)) : indexed),
    [indexed, needle],
  );
  const enabled = matching.filter((entry) => entry.enabled).map((entry) => entry.connector);
  const disabled = enabledOnly
    ? []
    : matching.filter((entry) => !entry.enabled).map((entry) => entry.connector);
  const shown = [...enabled, ...disabled];
  const total = enabledOnly ? indexed.filter((entry) => entry.enabled).length : indexed.length;

  if (connectors.isLoading) {
    return <SkyLoader size="large" label="Loading connectors…" labelPosition="below" />;
  }

  return (
    <div className="flex flex-col gap-4" data-testid="connector-picker">
      <SkyText as="h2" size={400} weight="semibold" block>
        Choose a source system
      </SkyText>
      <SkyText as="p" size={300} color="muted" block>
        Pick what this connection reads from. You can change everything else later. The connector is
        fixed once the connection is saved.
      </SkyText>
      {narrowed ? (
        <SkyText as="p" size={200} color="muted" block testId="connector-picker-limited">
          {CONNECTORS_LIMITED_NOTE}
        </SkyText>
      ) : null}

      {connectors.error ? (
        // A catalog that could not be fetched is not an empty catalog: saying
        // "no connectors are available" here would send an operator to the seed
        // script when the API is what is down. Same bar and wording as the
        // connections list, with a retry that refetches in place.
        <SkyMessageBar
          intent="error"
          layout="multiline"
          testId="connector-picker-error"
          title="We could not load connectors"
          message={failureMessage(
            connectors.error as ApiClientError,
            "We could not load connectors.",
          )}
          actions={
            <SkyButton size="small" icon={RetryIcon} onClick={() => void connectors.refetch()}>
              Try again
            </SkyButton>
          }
        />
      ) : (
        <>
          {/* A search landmark, so a screen-reader user can jump straight to it.
              The label is a real `<label>` rather than an `aria-label`: `SkyInput`
              drops props it does not declare, so an `aria-label` would silently
              leave the placeholder as the accessible name. `type="search"` gives
              the box the `searchbox` role, the right semantics inside the
              landmark. `clearable` is a real button labelled "Clear input" by
              the design system, so Escape is not the only way out. */}
          <div className="flex flex-wrap items-end gap-2">
            <div className="w-72" role="search" aria-label="Search connectors">
              <SkyInput
                testId="connector-picker-search"
                type="search"
                label="Search"
                placeholder="Search name, description or category"
                value={search}
                clearable
                contentBefore={<SearchIcon className="h-4 w-4 text-muted-foreground" />}
                onChange={(event) => setSearch(event.target.value)}
                onClear={() => setSearch("")}
              />
            </div>
            <SkySwitch
              label="Enabled only"
              checked={enabledOnly}
              onCheckedChange={setEnabledOnly}
              testId="connector-picker-enabled-only"
            />
            <div className="ml-auto">
              <SkyBadge appearance="ghost" color="subtle">
                <span aria-live="polite" data-testid="connector-count">
                  {shown.length} of {total} connectors
                </span>
              </SkyBadge>
            </div>
          </div>

          <ConnectorResults
            enabled={enabled}
            disabled={disabled}
            searchTerm={searchTerm}
            narrowed={narrowed}
            navigate={navigate}
          />
        </>
      )}
    </div>
  );
}
